import React from 'react';
import { MessageCircle, CalendarCheck2, UserRoundCheck, Bell, CalendarDays, Blocks } from 'lucide-react';

interface ServiceCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  index: number;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon: Icon, title, description, index }) => (
  <div className="group relative p-8 bg-mira-surface/40 backdrop-blur-sm border border-mira-border/50 rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-mira-accent/50 hover:shadow-[0_10px_40px_-10px_rgba(26,140,255,0.2)] h-full">
    
    {/* Background Gradient on Hover */}
    <div className="absolute inset-0 bg-gradient-to-br from-mira-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
    
    {/* Stylized Number Background */}
    <div className="absolute -right-4 -top-6 text-[120px] font-bold text-white/5 group-hover:text-mira-accent/10 transition-colors duration-500 select-none z-0">
      {index + 1}
    </div>

    <div className="relative z-10">
      {/* Icon Container */}
      <div className="w-14 h-14 mb-6 rounded-xl bg-mira-bg border border-mira-border flex items-center justify-center group-hover:border-mira-accent group-hover:bg-mira-accent/10 transition-all duration-300 shadow-lg">
        <Icon className="w-7 h-7 text-gray-300 group-hover:text-mira-accent transition-colors duration-300" />
      </div>

      <h3 className="text-xl md:text-2xl font-bold text-white mb-3 group-hover:text-mira-accent transition-colors duration-300 group-hover:drop-shadow-[0_0_8px_rgba(26,140,255,0.5)]">
        {title}
      </h3>
      
      <p className="text-gray-400 text-sm md:text-base leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
        {description}
      </p>
    </div>
  </div>
);

const Services: React.FC = () => {
  const services = [
    {
      icon: MessageCircle,
      title: "Chatbots para WhatsApp",
      description: "Atención automática 24/7 para tu negocio. Responde preguntas, agenda citas y atiende clientes sin que tú estés conectado."
    },
    {
      icon: CalendarCheck2,
      title: "Bots que Agendan Citas",
      description: "Tu negocio recibe citas solo, sin esfuerzo. El bot confirma horarios, envía recordatorios y evita cancelaciones."
    },
    {
      icon: UserRoundCheck,
      title: "Seguimiento Automático",
      description: "No pierdas clientes por no responder a tiempo. El sistema da seguimiento automático a consultas, cotizaciones y ventas."
    },
    {
      icon: Bell,
      title: "Recordatorios Programados",
      description: "Reduce cancelaciones y aumenta asistencia. El sistema manda mensajes automáticos antes de cada cita."
    },
    {
      icon: CalendarDays,
      title: "Conexión Google Calendar",
      description: "Tus citas se sincronizan solas. El bot agrega, cambia y cancela citas sin que lo hagas manualmente."
    },
    {
      icon: Blocks,
      title: "Integraciones a Medida",
      description: "Conectamos tu negocio con cualquier herramienta. CRM, Google Sheets, bases de datos o lo que uses."
    }
  ];

  return (
    <section className="py-24 relative overflow-hidden bg-[#0B0F1A]">
      {/* Ambient Lights */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[10%] left-[-10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[10%] right-[-10%] w-[500px] h-[500px] bg-mira-accent/10 rounded-full blur-[120px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20 animate-fade-up">
          <div className="inline-block px-3 py-1 mb-4 rounded-full border border-mira-accent/30 bg-mira-accent/5 shadow-[0_0_15px_rgba(26,140,255,0.2)]">
             <span className="text-xs font-bold text-mira-accent tracking-widest uppercase drop-shadow-[0_0_5px_rgba(26,140,255,0.5)]">Características</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold mb-6 text-white tracking-tight">
            Todo lo que necesitas para <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-mira-accent to-blue-300 drop-shadow-[0_0_15px_rgba(26,140,255,0.3)]">escalar tu negocio</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto font-medium">
            Herramientas potentes diseñadas para eliminar el trabajo manual y mejorar la experiencia de tus clientes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div key={index} className="animate-fade-up h-full" style={{ animationDelay: `${index * 100}ms` }}>
              <ServiceCard {...service} index={index} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;